import React, { Suspense, useEffect, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, ExternalLink, Flag, LocateFixed, Map as MapIcon, MapPin, MessageCircle, Navigation, Pencil, Phone, Trash2 } from 'lucide-react';
import { googleMapsDirLink, googleMapsDirTrechos, GOOGLE_MAPS_MAX_STOPS } from '../../../src/lib/routeOptimizer';
import { Parada, RotaAtiva, fmtDuracao, statusRotaBadge, statusRotaLabel, enderecoParada, urlNavegacao } from './rotaTypes';
import type { ParadaMapa } from './RotaMapa';
import { isRemota } from '../../../src/lib/modalidade';

// Lazy: o chunk do Leaflet só carrega quando algum card abre o mapa.
const RotaMapa = React.lazy(() => import('./RotaMapa'));

interface RotaExecucaoCardProps {
    rota: RotaAtiva;
    onOpenLead: (negociacaoId: number) => void;
    onIniciarRota: (rota: RotaAtiva) => void;
    onConcluirRota: (rota: RotaAtiva) => void;
    onCancelarRota: (rota: RotaAtiva) => void;
    /** Parada pendente → iniciar visita (motoboy abre modal c/ foto; comercial é instantâneo — decidido pelo pai). */
    onIniciarParada: (parada: Parada, rota: RotaAtiva) => void;
    /** Parada em visita → concluir SEMPRE pelo modal de encerramento (status + observação obrigatórios — pedido Bruno 10/jul). */
    onConcluirParada: (parada: Parada, rota: RotaAtiva) => void;
    /** Observações opcionais editáveis após a rota já existir (spec §3.1). */
    onSalvarObservacoes?: (rota: RotaAtiva, texto: string) => void | Promise<void>;
    /** Reordenação manual pós-otimização, só quando planejada (spec §5). */
    onReordenarParada?: (rota: RotaAtiva, paradaId: string, dir: -1 | 1) => void | Promise<void>;
    /** Re-ancora as pendentes no GPS atual do usuário (rota em execução, meio do dia). */
    onReordenarDaqui?: (rota: RotaAtiva) => void | Promise<void>;
    /** Destaque visual da rota recém-criada. */
    destacar?: boolean;
}

/** Próxima parada da rota em execução: prioriza a que já está em visita, senão a 1ª pendente. */
const proximaParada = (rota: RotaAtiva): Parada | null => {
    if (rota.status !== 'em_execucao') return null;
    const ordenadas = [...rota.rota_paradas].sort((a, b) => a.posicao - b.posicao);
    return ordenadas.find(p => p.status === 'em_visita') || ordenadas.find(p => p.status === 'pendente') || null;
};

/**
 * Card de execução de UMA rota comercial. Renderizado empilhado — o vendedor pode ter
 * várias rotas ativas ao mesmo tempo, cada uma iniciada/concluída/cancelada de forma independente.
 */
const RotaExecucaoCard: React.FC<RotaExecucaoCardProps> = ({ rota, onOpenLead, onIniciarRota, onConcluirRota, onCancelarRota, onIniciarParada, onConcluirParada, onSalvarObservacoes, onReordenarParada, onReordenarDaqui, destacar }) => {
    const visitadas = rota.rota_paradas.filter(p => p.status === 'visitado').length;
    const total = rota.rota_paradas.length;
    const naoIniciada = rota.status === 'planejada' || rota.status === 'agendada' || rota.status === 'reagendada';
    const pendentesComGeo = rota.rota_paradas
        .filter(p => p.status === 'pendente' && p.negociacoes?.latitude)
        .map(p => ({ lat: p.negociacoes!.latitude!, lng: p.negociacoes!.longitude! }));
    // Ponto de chegada da rota (Prospecção). Sem ele o Maps terminaria na última empresa.
    const destinoRota = (rota as any).destino_lat != null && (rota as any).destino_lng != null
        ? { lat: Number((rota as any).destino_lat), lng: Number((rota as any).destino_lng) }
        : null;
    const mapsLink = googleMapsDirLink(pendentesComGeo, undefined, destinoRota);
    // Google Maps aceita 10 paradas por rota (limite do produto) — o link principal leva
    // as PRÓXIMAS 10 e a rota completa fica disponível em trechos encadeados. Com chegada
    // definida sobram 9: o destino ocupa uma das vagas.
    const tetoMaps = destinoRota ? GOOGLE_MAPS_MAX_STOPS - 1 : GOOGLE_MAPS_MAX_STOPS;
    const mapsTruncado = pendentesComGeo.length > tetoMaps;
    const mapsTrechos = mapsTruncado ? googleMapsDirTrechos(pendentesComGeo, destinoRota) : [];
    const proxima = proximaParada(rota);
    const proximaTel = (proxima?.negociacoes?.whatsapp || '').replace(/\D/g, '');

    // Visão completa da rota no mapa embutido (o Google Maps corta em 10 por link).
    const paradasMapa: ParadaMapa[] = rota.rota_paradas
        .filter(p => p.negociacoes?.latitude && p.negociacoes?.longitude)
        .map(p => ({
            id: p.id, posicao: p.posicao, status: p.status,
            lat: p.negociacoes!.latitude!, lng: p.negociacoes!.longitude!,
            titulo: p.negociacoes?.titulo || `Parada ${p.posicao}`,
            endereco: enderecoParada(p.negociacoes) || undefined,
        }));
    const semGeo = total - paradasMapa.length;
    // ≠ proximaParada(): quem está em_visita JÁ está no local — navegar mira a 1ª pendente com geo.
    const proximaNav = [...rota.rota_paradas].sort((a, b) => a.posicao - b.posicao)
        .find(p => p.status === 'pendente' && p.negociacoes?.latitude && p.negociacoes?.longitude) || null;

    const [editObs, setEditObs] = useState(false);
    const [obs, setObs] = useState(rota.observacoes || '');
    const [mapaAberto, setMapaAberto] = useState(false);
    const [reordenando, setReordenando] = useState(false);
    useEffect(() => { setObs(rota.observacoes || ''); }, [rota.observacoes]);

    return (
        <div className={`bg-white dark:bg-gray-800 rounded-2xl border shadow-sm p-5 transition-all ${destacar ? 'border-primary ring-2 ring-primary/40' : 'border-gray-100 dark:border-gray-700'}`}>
            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <Flag size={15} className="text-emerald-500" />
                {rota.nome || (rota.status === 'em_execucao' ? 'Rota em Execução' : rota.origem === 'inteligente' ? 'Rota Inteligente' : statusRotaLabel(rota.status))}
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusRotaBadge(rota.status)}`}>{statusRotaLabel(rota.status)}</span>
            </h2>

            {/* Observações da rota — opcionais, editáveis depois da criação */}
            {editObs ? (
                <div className="mb-3 flex items-start gap-2">
                    <textarea
                        value={obs}
                        onChange={e => setObs(e.target.value)}
                        rows={2}
                        autoFocus
                        placeholder="Observações da rota (opcional)"
                        className="flex-1 px-2.5 py-1.5 text-xs rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                    />
                    <button
                        onClick={() => { onSalvarObservacoes?.(rota, obs); setEditObs(false); }}
                        className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-primary text-white hover:opacity-90 shrink-0"
                    >
                        Salvar
                    </button>
                </div>
            ) : rota.observacoes ? (
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2 italic flex items-start gap-1.5">
                    <span className="flex-1">"{rota.observacoes}"</span>
                    {onSalvarObservacoes && rota.status !== 'concluida' && rota.status !== 'cancelada' && (
                        <button onClick={() => setEditObs(true)} className="text-gray-400 hover:text-primary shrink-0" title="Editar observações"><Pencil size={12} /></button>
                    )}
                </p>
            ) : onSalvarObservacoes && rota.status !== 'concluida' && rota.status !== 'cancelada' ? (
                <button onClick={() => setEditObs(true)} className="text-[11px] text-gray-400 hover:text-primary mb-2 flex items-center gap-1"><Pencil size={11} /> Adicionar observações</button>
            ) : null}

            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
                <span>
                    {new Date(rota.data + 'T12:00:00').toLocaleDateString('pt-BR')} · {total} paradas · ~{Math.round(rota.distancia_total_km || 0)} km
                    {rota.duracao_min ? ` · 🚦 ~${fmtDuracao(rota.duracao_min)}` : ''}
                </span>
                <span>{visitadas}/{total}</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden mb-4">
                <div
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ width: `${total > 0 ? (visitadas / total) * 100 : 0}%` }}
                ></div>
            </div>

            {/* Próxima parada SEMPRE no topo, com ligar/WhatsApp (adendo Bruno §3) */}
            {proxima && (
                <div className={`mb-3 p-3 rounded-xl border ${proxima.status === 'em_visita' ? 'border-blue-300 dark:border-blue-700 bg-blue-50/70 dark:bg-blue-900/20' : 'border-primary/40 bg-primary/5'}`}>
                    <p className={`text-[9px] font-extrabold uppercase tracking-widest mb-1 ${proxima.status === 'em_visita' ? 'text-blue-600 dark:text-blue-400' : 'text-primary'}`}>
                        {proxima.status === 'em_visita' ? '● Em visita agora' : '▸ Próxima parada'}
                    </p>
                    <button
                        onClick={() => proxima.negociacao_id && onOpenLead(proxima.negociacao_id)}
                        className="text-sm font-bold text-gray-900 dark:text-white hover:text-primary text-left leading-tight"
                        title={proxima.negociacao_id ? 'Abrir lead' : undefined}
                    >
                        {proxima.negociacoes?.titulo || `Parada ${proxima.posicao}`}
                    </button>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                        {enderecoParada(proxima.negociacoes) || 'Sem endereço'}
                    </p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {proximaTel && (
                            <>
                                <a href={`tel:+55${proximaTel}`} className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors">
                                    <Phone size={11} /> Ligar
                                </a>
                                <a href={`https://wa.me/55${proximaTel}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/60 transition-colors">
                                    <MessageCircle size={11} /> WhatsApp
                                </a>
                            </>
                        )}
                        {urlNavegacao(proxima.negociacoes) ? (
                            <a href={urlNavegacao(proxima.negociacoes)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                <Navigation size={11} /> Navegar
                            </a>
                        ) : null}
                        {proxima.status === 'pendente' ? (
                            <button
                                onClick={() => onIniciarParada(proxima, rota)}
                                className="ml-auto flex items-center gap-1 px-3.5 py-1.5 text-[11px] font-bold rounded-full bg-primary text-white hover:opacity-90 transition-opacity"
                            >
                                Iniciar Visita
                            </button>
                        ) : (
                            <button
                                onClick={() => onConcluirParada(proxima, rota)}
                                title="Concluir a visita — registra status e observação"
                                className="ml-auto flex items-center gap-1 px-3.5 py-1.5 text-[11px] font-bold rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                            >
                                <CheckCircle2 size={12} /> Concluir
                            </button>
                        )}
                    </div>
                </div>
            )}

            {paradasMapa.length > 0 && (
                <button
                    onClick={() => setMapaAberto(v => !v)}
                    className="flex items-center gap-1 text-[11px] font-semibold text-gray-500 dark:text-gray-400 hover:text-primary mb-2 transition-colors"
                >
                    <MapIcon size={12} /> {mapaAberto ? 'Ocultar mapa' : `Ver mapa da rota (${paradasMapa.length})`}
                </button>
            )}
            {mapaAberto && (
                <Suspense fallback={<div className="h-56 sm:h-72 rounded-xl bg-gray-100 dark:bg-gray-700 animate-pulse mb-3" />}>
                    <RotaMapa paradas={paradasMapa} origem={rota.snapshot_otimizacao?.origin} semGeo={semGeo} />
                </Suspense>
            )}

            <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1 mb-4">
                {rota.rota_paradas.map((p, i) => (
                    <div key={p.id} className={`flex items-center gap-2.5 p-2.5 rounded-xl ${p.status === 'visitado' ? 'bg-emerald-50 dark:bg-emerald-900/20' : p.status === 'em_visita' ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-200 dark:ring-blue-800' : 'bg-gray-50 dark:bg-gray-700/40'}`}>
                        <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${p.status === 'visitado' ? 'bg-emerald-500 text-white' : p.status === 'em_visita' ? 'bg-blue-500 text-white animate-pulse' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'}`}>
                            {p.status === 'visitado' ? '✓' : p.posicao}
                        </span>
                        <div
                            className={`min-w-0 flex-1 ${p.negociacao_id ? 'cursor-pointer' : ''}`}
                            onClick={() => p.negociacao_id && onOpenLead(p.negociacao_id)}
                            title={p.negociacao_id ? 'Abrir lead' : undefined}
                        >
                            <p className={`text-xs font-semibold truncate flex items-center gap-1.5 ${p.status === 'visitado' ? 'text-emerald-700 dark:text-emerald-300 line-through' : 'text-gray-800 dark:text-gray-100 hover:text-primary'}`}>
                                {isRemota(p.modalidade) ? <Phone size={11} className="text-purple-500 shrink-0" /> : <MapPin size={11} className="text-emerald-500 shrink-0" />}
                                {p.negociacoes?.titulo || `Parada ${p.posicao}`}
                            </p>
                            <p className="text-[10px] text-gray-400 truncate">
                                {p.status === 'em_visita' && p.checkin_em
                                    ? `Em visita desde ${new Date(p.checkin_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
                                    : [p.negociacoes?.bairro, p.negociacoes?.cidade].filter(Boolean).join(', ')}
                            </p>
                        </div>
                        {/* Reordenação manual (só quando a rota ainda não foi iniciada) */}
                        {naoIniciada && onReordenarParada && total > 1 && (
                            <div className="flex flex-col shrink-0">
                                <button disabled={i === 0} onClick={() => onReordenarParada(rota, p.id, -1)} className="text-gray-400 hover:text-primary disabled:opacity-20" title="Subir"><ChevronUp size={14} /></button>
                                <button disabled={i === total - 1} onClick={() => onReordenarParada(rota, p.id, 1)} className="text-gray-400 hover:text-primary disabled:opacity-20" title="Descer"><ChevronDown size={14} /></button>
                            </div>
                        )}
                        {rota.status === 'em_execucao' && p.status === 'pendente' && (
                            <button
                                onClick={() => onIniciarParada(p, rota)}
                                className="text-[10px] font-bold px-2.5 py-1.5 rounded-full bg-primary text-white hover:opacity-90 transition-opacity shrink-0"
                            >
                                Iniciar Visita
                            </button>
                        )}
                        {rota.status === 'em_execucao' && p.status === 'em_visita' && (
                            <span className="flex items-center gap-1 shrink-0">
                                <button
                                    onClick={() => onConcluirParada(p, rota)}
                                    title="Concluir a visita — registra status e observação"
                                    className="text-[10px] font-bold px-2.5 py-1.5 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                                >
                                    ✓ Concluir
                                </button>
                                <button
                                    onClick={() => onConcluirParada(p, rota)}
                                    title="Concluir com detalhes (status, observação, próxima ação)"
                                    className="p-1.5 rounded-full text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors"
                                >
                                    <Pencil size={12} />
                                </button>
                            </span>
                        )}
                    </div>
                ))}
            </div>

            <div className="space-y-2">
                {naoIniciada ? (
                    <button onClick={() => onIniciarRota(rota)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl bg-primary text-white hover:opacity-90 transition-opacity">
                        <Navigation size={15} /> Iniciar Rota ({total})
                    </button>
                ) : (
                    <button onClick={() => onConcluirRota(rota)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl bg-emerald-500 text-white hover:bg-emerald-600 transition-colors">
                        <CheckCircle2 size={15} /> Concluir Rota
                    </button>
                )}
                {rota.status === 'em_execucao' && proximaNav && (
                    <a
                        href={urlNavegacao(proximaNav.negociacoes)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                        <Navigation size={15} /> Navegar p/ próxima parada (#{proximaNav.posicao})
                    </a>
                )}
                {onReordenarDaqui && rota.status === 'em_execucao' && pendentesComGeo.length >= 2 && (
                    <button
                        onClick={async () => { setReordenando(true); try { await onReordenarDaqui(rota); } finally { setReordenando(false); } }}
                        disabled={reordenando}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                    >
                        <LocateFixed size={14} /> {reordenando ? 'Reordenando…' : 'Reordenar a partir de onde estou'}
                    </button>
                )}
                {mapsLink && (
                    <>
                        <a
                            href={mapsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            <ExternalLink size={14} />
                            {mapsTruncado
                                ? `Rota no Google Maps (próximas ${GOOGLE_MAPS_MAX_STOPS} de ${pendentesComGeo.length})`
                                : 'Rota no Google Maps'}
                        </a>
                        {mapsTruncado && (
                            <details className="rounded-xl bg-gray-50 dark:bg-gray-700/40 px-3.5 py-2.5">
                                <summary className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 cursor-pointer select-none">
                                    Rota completa em trechos ({mapsTrechos.length}) — o Google Maps aceita {GOOGLE_MAPS_MAX_STOPS} paradas por rota
                                </summary>
                                <p className="text-[10px] text-gray-400 mt-1.5 mb-2">O fim de um trecho é o início do seguinte — termine um e abra o próximo.</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {mapsTrechos.map(t => (
                                        <a
                                            key={t.ini}
                                            href={t.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-2.5 py-1.5 text-[11px] font-bold rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary hover:text-primary transition-colors"
                                        >
                                            {t.ini}–{t.fim}
                                        </a>
                                    ))}
                                </div>
                            </details>
                        )}
                    </>
                )}
                <button onClick={() => onCancelarRota(rota)} className="w-full flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                    <Trash2 size={13} /> Cancelar rota
                </button>
            </div>
        </div>
    );
};

export default RotaExecucaoCard;
